import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Lock } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Alert, Button, Field, Input, LoadingBlock } from '@/components/ui';
import { AuthCard } from './AuthCard';

export default function PasswortZuruecksetzenPage() {
  const [params] = useSearchParams();
  // Token steht im Fragment (#token=…) und wird so nie an Server übertragen; ältere Links mit ?token= bleiben gültig
  const [token] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') ?? params.get('token') ?? '');
  // Token nach dem Lesen aus der Adresszeile entfernen (im Effekt, damit ein doppelter Initialisierer-Aufruf ihn nicht verliert)
  useEffect(() => {
    if (window.location.hash || window.location.search) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const check = useQuery({
    queryKey: ['reset-token', token],
    queryFn: () => api.post<{ valid: boolean; email?: string; purpose?: 'RESET' | 'EINLADUNG' }>('/auth/reset-password/check', { token }),
    enabled: token.length >= 20,
    retry: false,
    staleTime: Infinity,
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== repeat) {
      setError('Die beiden Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Das Passwort konnte nicht gesetzt werden.');
    } finally {
      setBusy(false);
    }
  };

  const invalid = token.length < 20 || check.isError || (check.data && !check.data.valid);
  const invitation = check.data?.purpose === 'EINLADUNG';

  return (
    <AuthCard
      title={invitation ? 'Willkommen im Mieterportal' : 'Neues Passwort festlegen'}
      subtitle={
        check.data?.valid && !done
          ? invitation
            ? `Legen Sie Ihr persönliches Passwort fest. Anmeldung künftig mit ${check.data.email}.`
            : `Für ${check.data.email}`
          : undefined
      }
    >
      {done ? (
        <div className="space-y-4">
          <Alert tone="success" title={invitation ? 'Passwort festgelegt' : 'Passwort geändert'}>
            {invitation
              ? 'Ihr Passwort ist gespeichert. Melden Sie sich jetzt mit Ihrer E-Mail-Adresse und dem neuen Passwort an.'
              : 'Ihr neues Passwort ist gespeichert. Aus Sicherheitsgründen wurden alle bestehenden Anmeldungen beendet.'}
          </Alert>
          <Button variant="primary" className="w-full" onClick={() => navigate('/login', { replace: true })}>
            Jetzt anmelden
          </Button>
        </div>
      ) : invalid ? (
        <div className="space-y-4">
          <Alert tone="error" title="Link ungültig">
            Der Link ist ungültig, abgelaufen oder wurde bereits verwendet. Bitte fordern Sie einen neuen Link an – bei einer Einladung ins Mieterportal wenden Sie sich an Ihre Hausverwaltung.
          </Alert>
          <Link to="/passwort-vergessen" className="inline-flex text-sm font-medium text-brand-700 hover:underline">
            Neuen Link anfordern
          </Link>
        </div>
      ) : check.isLoading ? (
        <LoadingBlock />
      ) : (
        <form onSubmit={submit} className="space-y-5">
          {error && <Alert tone="error">{error}</Alert>}
          <Field label="Neues Passwort" htmlFor="pw" hint="Mindestens 12 Zeichen, mit Groß- und Kleinbuchstaben sowie einer Ziffer.">
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input id="pw" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" autoFocus />
            </div>
          </Field>
          <Field label="Neues Passwort wiederholen" htmlFor="pw2">
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input id="pw2" type="password" autoComplete="new-password" required value={repeat} onChange={(e) => setRepeat(e.target.value)} className="pl-9" />
            </div>
          </Field>
          <Button type="submit" variant="primary" className="w-full" loading={busy}>
            Passwort speichern
          </Button>
        </form>
      )}
      {!done && (
        <Link to="/login" className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Zur Anmeldung
        </Link>
      )}
    </AuthCard>
  );
}
